import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'

export const DEEP_LINK_PATH_PARAM = 'u'

// getLaunchUrl() keeps returning the original launch URL for the whole app
// lifecycle. A logout triggers a webview reload (window.location.replace), which
// would replay a stale OAuth callback deep link, failing sign-in and showing a
// spurious error toast. The sessionStorage flag survives the reload but is
// cleared on app kill, so the launch URL is only honored on a true cold start.
// Live deep links go through appUrlOpen instead.
const LAUNCH_URL_CONSUMED_KEY = 'launchUrlConsumed'

// Share links emit `u=release/42` while the SSO callback emits `u=/auth/...`. A
// path without the leading slash is resolved RELATIVE to the route the app is on,
// which for a running app is almost never the root, so it lands on a path no route
// matches. Leading slashes are collapsed to exactly one so a link can neither stay
// relative nor turn into a protocol-relative '//host' location.
const toAbsolutePath = (path: string): string => `/${path.replace(/^\/+/, '')}`

export const readDeepLinkPath = (url: string): string | null => {
  const path = new URL(url).searchParams.get(DEEP_LINK_PATH_PARAM)
  return path ? toAbsolutePath(path) : null
}

const readNativeLaunchDeepLinkPath = async (): Promise<string | null> => {
  const result = await CapacitorApp.getLaunchUrl().catch((): null => null)
  if (!result?.url) {
    return null
  }
  if (sessionStorage.getItem(LAUNCH_URL_CONSUMED_KEY)) {
    return null
  }
  sessionStorage.setItem(LAUNCH_URL_CONSUMED_KEY, '1')
  return readDeepLinkPath(result.url)
}

export const takeStartupDeepLinkPath = async (): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) {
    const path = new URLSearchParams(window.location.search).get(DEEP_LINK_PATH_PARAM)
    return path ? toAbsolutePath(path) : null
  }
  return readNativeLaunchDeepLinkPath()
}
